import { useCallback, useMemo } from "react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AirCfarSelectors,
  AirChfarSelectors,
  FlightSelectors,
  VipSupportSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  AirRestrictionStatus,
  type AirportMap,
  type FareDetails,
  type Restriction as RestrictionType,
  type TripDetails,
} from "@hopper-b2b/types";
import {
  IconComponent,
  IconContentView,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import {
  type OverwriteType,
  getEmptyRestrictionsText,
  getEnvVariables,
  getRestrictions,
  getSliceIndex,
  useDeviceTypes,
  useEnableDarkMode,
} from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";

import "./styles.scss";
import { MultiTicketType } from "@b2bportal/air-booking-api";

export interface FareDetailsModalProps {
  open: boolean;
  onClose: () => void;
}

export const FareDetailsModal = ({ open, onClose }: FareDetailsModalProps) => {
  const { t: translate } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const enabledDarkMode = useEnableDarkMode();
  const darkModePreferred = getDarkModePreferred();

  const vipSupportEnabled = useSelector(
    VipSupportSelectors.isVipSupportEnabled
  );
  const cfarQuoteId = useSelector(AirCfarSelectors.getCfarQuoteId);
  const chfarQuoteId = useSelector(AirChfarSelectors.getChfarQuoteId);

  const departureDate = useSelector(
    FlightSelectors.getFlightSearchDepartureDate
  );
  const returnDate = useSelector(FlightSelectors.getFlightSearchReturnDate);
  const airports = useSelector(FlightSelectors.getAirportMap);
  const isOneWay = useSelector(FlightSelectors.getIsSelectedTripOneWay);
  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const fareDetails = useSelector(FlightSelectors.getSelectedFareDetails);
  const firstSliceIndex = getSliceIndex(true, tripDetails);
  const secondSliceIndex = getSliceIndex(false, tripDetails);
  const multiTicketType = useSelector(FlightSelectors.getFlightMultiTicketType);
  const departureRestrictions = useMemo(() => {
    if (fareDetails) {
      return getRestrictions({
        fareDetails,
        sliceIndex: firstSliceIndex,
        translate,
        customerSupport: vipSupportEnabled,
        cfarSelected: !!cfarQuoteId,
        chfarSelected: !!chfarQuoteId,
      });
    }
    return null;
  }, [
    cfarQuoteId,
    chfarQuoteId,
    fareDetails,
    firstSliceIndex,
    translate,
    vipSupportEnabled,
  ]);

  const returnRestrictions = useMemo(() => {
    if (fareDetails && !isOneWay) {
      return getRestrictions({
        fareDetails,
        sliceIndex: secondSliceIndex,
        translate,
        customerSupport: vipSupportEnabled,
        cfarSelected: !!cfarQuoteId,
        chfarSelected: !!chfarQuoteId,
      });
    }
    return null;
  }, [
    cfarQuoteId,
    chfarQuoteId,
    fareDetails,
    isOneWay,
    secondSliceIndex,
    translate,
    vipSupportEnabled,
  ]);

  return (
    <MobilePopoverCard
      className={clsx("fare-details-popup", {
        mobile: matchesMobile,
        "dark-mode": enabledDarkMode && darkModePreferred,
      })}
      contentClassName="fare-details-content"
      open={open}
      fullScreen={false}
      centered
      onClose={onClose}
      headerElement={translate("flightsPageTitles.fareDetails")}
      topRightButton={
        <button className="terms-close" onClick={onClose}>
          <IconComponent className="terms-close-icon" name={IconName.Close} />
        </button>
      }
    >
      {multiTicketType !== MultiTicketType.single ? (
        <div className="multi-ticket-section">
          <h2 className="multi-ticket-title">
            {translate("flightShopReview.multiTicketTitle")}
          </h2>
          <p className="multi-ticket-subtitle">
            {translate("flightShopReview.multiTicketSubtitle")}
          </p>
        </div>
      ) : null}
      <UberRestrictionsSection
        fareDetails={fareDetails}
        tripDetails={tripDetails}
        isOneWay={isOneWay}
        returnDate={returnDate}
        departureDate={departureDate}
        airports={airports}
        departureRestrictions={departureRestrictions}
        returnRestrictions={returnRestrictions}
      />
    </MobilePopoverCard>
  );
};

export interface UberRestrictionsSectionProps {
  fareDetails: FareDetails | undefined;
  tripDetails: TripDetails;
  isOneWay: boolean;
  returnDate: Date | null;
  departureDate: Date | null;
  airports: AirportMap;
  departureRestrictions: RestrictionType[] | null;
  returnRestrictions: RestrictionType[] | null;
}

export const UberRestrictionsSection = ({
  fareDetails,
  tripDetails,
  isOneWay,
  returnDate,
  departureDate,
  airports,
  departureRestrictions,
  returnRestrictions,
}: UberRestrictionsSectionProps) => {
  const { t: translate } = useI18nContext();
  const firstSliceIndex = getSliceIndex(true, tripDetails);
  const secondSliceIndex = getSliceIndex(false, tripDetails);

  const getReviewCardHeaderWithTypeLocation = airports[
    tripDetails.slices[firstSliceIndex].destinationCode
  ]
    ? `${
        airports[tripDetails.slices[firstSliceIndex].destinationCode].cityName
      } (${airports[tripDetails.slices[firstSliceIndex].destinationCode].code})`
    : tripDetails.slices[firstSliceIndex].destinationName;

  const getRestrictionsList = useCallback(
    (restrictions: RestrictionType[] | null, index: number) => {
      if (restrictions && restrictions?.length > 0) {
        return restrictions?.map((restriction) => (
          <RestrictionItem
            key={restriction.name}
            symbol={restriction.symbol}
            name={restriction.name}
            description={
              <Typography className="description">
                {restriction.description}
              </Typography>
            }
          />
        ));
      } else {
        return (
          <div className="empty-restrictions">
            {getEmptyRestrictionsText(
              fareDetails?.slices[index]?.fareShelf?.rating,
              translate
            )}
          </div>
        );
      }
    },
    [fareDetails?.slices, translate]
  );

  if (isOneWay) {
    return (
      <div className="restrictions-section">
        {fareDetails
          ? getRestrictionsList(departureRestrictions, firstSliceIndex)
          : null}
      </div>
    );
  } else {
    return (
      <>
        <div className="restrictions-section outbound-section">
          <Typography className="fare-slice-header" variant="body1">
            <I18nMarkup
              tKey={
                getEnvVariables("clientName") === "uber"
                  ? "flightShopReview.outboundCardHeader"
                  : "flightShopReview.departureCardHeader"
              }
              replacements={{
                location: getReviewCardHeaderWithTypeLocation,
                date: dayjs(departureDate).format("ddd, MMM D"),
              }}
            />
            <p className="fare-slice-header-airline">
              {tripDetails.slices[0].segmentDetails[0].airlineName}
            </p>
          </Typography>

          {fareDetails
            ? getRestrictionsList(departureRestrictions, firstSliceIndex)
            : null}
        </div>
        <div className="restrictions-section">
          <Typography className="fare-slice-header" variant="body1">
            <I18nMarkup
              tKey={"flightShopReview.returnCardHeader"}
              replacements={{
                location: airports[
                  tripDetails.slices[secondSliceIndex].destinationCode
                ]
                  ? airports[
                      tripDetails.slices[secondSliceIndex].destinationCode
                    ].cityName
                  : tripDetails.slices[secondSliceIndex].destinationName,
                date: dayjs(returnDate).format("ddd, MMM D"),
              }}
            />
            <p className="fare-slice-header-airline">
              {tripDetails.slices[1].segmentDetails[0].airlineName}
            </p>
          </Typography>

          {fareDetails
            ? getRestrictionsList(returnRestrictions, secondSliceIndex)
            : null}
        </div>
      </>
    );
  }
};

const uberRestrictionIcon: { [k in AirRestrictionStatus]: JSX.Element } = {
  [AirRestrictionStatus.INCLUDED]: (
    <FontAwesomeIcon
      className="icon-available"
      icon={faCheckCircle as IconProp}
    />
  ),
  //TODO: Make this dynamic based on currency
  [AirRestrictionStatus.PURCHASABLE]: (
    <IconComponent className="icon-paid" name={IconName.PoundCurrencySymbol} />
  ),
  [AirRestrictionStatus.UNAVAILABLE]: (
    <IconComponent className="icon-unavailable" name={IconName.XCircleFilled} />
  ),
  [AirRestrictionStatus.UNKNOWN]: null,
  [AirRestrictionStatus.GENERIC]: null,
};

export const RestrictionItem = ({
  symbol,
  name,
  description,
}: OverwriteType<RestrictionType, { description: JSX.Element }>) => {
  const selectedCfarOffer = useSelector(AirCfarSelectors.getSelectedCfarOffer);
  const selectedChfarOffer = useSelector(
    AirChfarSelectors.getSelectedChfarOffer
  );

  let icon = uberRestrictionIcon[symbol];
  const cfarSelected = name === "Cancellation" && selectedCfarOffer;
  const chfarSelected = name === "Advance Change" && selectedChfarOffer;

  if (cfarSelected || chfarSelected) {
    icon = uberRestrictionIcon[AirRestrictionStatus.INCLUDED];
  }

  return (
    <IconContentView
      className="restriction"
      icon={icon}
      content={
        <>
          <Typography className="title">{name}</Typography>
          <Typography variant="caption">{description}</Typography>
        </>
      }
    />
  );
};
